/**
 * `<DesktopUpdateWatcher>` — on the desktop build, checks for an app update once
 * on launch and, if one is available, asks the user (branded confirm) before
 * downloading + installing + relaunching. No-op on web and on older shells that
 * predate the updater plugin.
 *
 * Mounted at the app root (a sibling of `<ToastHost>`), so it runs regardless of
 * route or auth state — updating the native shell is not gated on sign-in, and
 * we want the check to fire even while the user sits on the sign-in screen.
 *
 * We never auto-install silently: the user always confirms (the install
 * downloads a binary and restarts the app). The manual "Check for updates"
 * control lives in Settings → Software update.
 */

import { useEffect, useRef, useState } from 'react';
import { useConfirmModal } from '@/components';
import { checkForUpdate, installUpdateAndRelaunch } from './updater';

export function DesktopUpdateWatcher(): JSX.Element {
  const { modal, confirm } = useConfirmModal();
  const ranRef = useRef(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Guard against StrictMode's double-invoke + route remounts — check once.
    if (ranRef.current) return;
    ranRef.current = true;

    let cancelled = false;
    void (async () => {
      const update = await checkForUpdate();
      if (cancelled || !update) return;

      const ok = await confirm({
        eyebrow: 'Update',
        title: `Update to ${update.version}`,
        message:
          `A new version of Aletheia (${update.version}) is available — ` +
          `you're on ${update.currentVersion}. Install now? Aletheia will ` +
          `download it and restart.`,
        confirmLabel: 'Install & restart',
        cancelLabel: 'Later',
      });
      if (cancelled || !ok) return;

      setInstalling(true);
      try {
        await installUpdateAndRelaunch(update);
        // On success the process relaunches, so we never get here.
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[updater] install failed', err);
        setInstalling(false);
        await confirm({
          eyebrow: 'Update',
          title: 'Update failed',
          message:
            'The update could not be installed. You can retry from ' +
            'Settings → Software update, or download the latest build manually.',
          confirmLabel: 'OK',
          cancelLabel: 'Dismiss',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [confirm]);

  return (
    <>
      {modal}
      {installing && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            zIndex: 9999,
            padding: '10px 14px',
            borderRadius: 8,
            background: 'var(--bg-2)',
            color: 'var(--fg)',
            border: '1px solid var(--line)',
            boxShadow: 'var(--shadow-md)',
            font: '500 13px var(--font-mono, monospace)',
          }}
        >
          Installing update… Aletheia will restart.
        </div>
      )}
    </>
  );
}
